import { HttpInterceptorFn, HttpErrorResponse, HttpRequest, HttpHandlerFn } from '@angular/common/http';
import { inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { AuthService } from '../../features/music/services/auth.service';
import { switchMap, catchError, timeout, tap } from 'rxjs/operators';
import { throwError, Observable } from 'rxjs';
import { shareReplay, finalize } from 'rxjs/operators';

// Server-side anonymous token cache (persists across requests in the same Node process)
let serverAnonymousToken: string | null = null;
// Shared observable for deduplicating concurrent anonymous login calls
let sharedAnonymousLogin$: Observable<any> | null = null;
// Flag to prevent re-entrant 401 handling (avoids infinite loops)
let isRefreshing = false;

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const platformId = inject(PLATFORM_ID);
  const isBrowser = isPlatformBrowser(platformId);

  // Skip token injection for auth endpoints to avoid loops
  if (req.url.includes('/user-api/anonymous-login/') ||
    req.url.includes('/user-api/auth/token/refresh/') ||
    req.url.includes('/user-api/auth/login/')) {
    return next(req);
  }

  // Skip token injection for external URLs (S3, CDN, etc.)
  if (req.url.includes('amazonaws.com') ||
    req.url.includes('s3.') ||
    req.url.includes('cloudfront.net') ||
    (!req.url.includes('colorfol.com') && req.url.startsWith('http'))) {
    return next(req);
  }

  // Helper: perform anonymous login with deduplication
  const doAnonymousLogin = (): Observable<any> => {
    if (!sharedAnonymousLogin$) {
      let loginReq$ = authService.anonymousLogin();

      // Server should fail fast
      if (!isBrowser) {
        loginReq$ = loginReq$.pipe(timeout(8000));
      }

      sharedAnonymousLogin$ = loginReq$.pipe(
        shareReplay(1),
        finalize(() => {
          // Reset after all subscribers have consumed the result
          // This allows a fresh login on next need (e.g., after token expires)
          sharedAnonymousLogin$ = null;
        })
      );
    }
    return sharedAnonymousLogin$;
  };

  // Helper: execute request with 401 error handling
  const handleRequest = (request: HttpRequest<any>, nextFn: HttpHandlerFn) => {
    let request$ = nextFn(request);

    // SSR: fail fast with 5s timeout instead of waiting for API 504s
    if (!isBrowser) {
      request$ = request$.pipe(timeout(8000));
    }

    return request$.pipe(
      catchError((error) => {
        // Only handle 401 in browser — server should fail fast
        if (!isBrowser || !(error instanceof HttpErrorResponse) || error.status !== 401 || isRefreshing) {
          return throwError(() => error);
        }

        isRefreshing = true;

        // Check if current user is anonymous
        const currentUser = authService.getCurrentUser();
        const isAnonymous = !currentUser || currentUser.is_anonymous;

        if (isAnonymous) {
          // Anonymous user: redo anonymous login instead of token refresh
          return doAnonymousLogin().pipe(
            switchMap((response) => {
              isRefreshing = false;
              const newAuthReq = request.clone({
                setHeaders: { Authorization: `Bearer ${response.access_token}` }
              });
              return nextFn(newAuthReq);
            }),
            catchError((loginError) => {
              isRefreshing = false;
              console.error('Anonymous re-login failed:', loginError);
              return throwError(() => error);
            })
          );
        }

        // Authenticated user: try refresh token
        const refreshToken = authService.getRefreshToken();
        if (refreshToken) {
          return authService.refreshToken(refreshToken).pipe(
            switchMap((response) => {
              isRefreshing = false;
              authService.updateToken(response.access);
              const newAuthReq = request.clone({
                setHeaders: { Authorization: `Bearer ${response.access}` }
              });
              return nextFn(newAuthReq);
            }),
            catchError((refreshError) => {
              isRefreshing = false;
              console.error('RefreshToken failed:', refreshError);
              authService.logout();
              return throwError(() => refreshError);
            })
          );
        }

        // No refresh token available
        isRefreshing = false;
        return throwError(() => error);
      })
    );
  };

  // Helper: attach token and execute request
  const executeWithToken = (token: string) => {
    const authReq = req.clone({
      setHeaders: { Authorization: `Bearer ${token}` }
    });
    return handleRequest(authReq, next);
  };

  // 1. Get token — browser: localStorage, server: in-memory cache
  const token = isBrowser ? authService.getAccessToken() : serverAnonymousToken;

  // 2. If token exists, use it
  if (token) {
    return executeWithToken(token);
  }

  // 3. Browser: if refresh token exists, proceed without access token (401 handler will fix it)
  if (isBrowser && authService.getRefreshToken()) {
    return handleRequest(req, next);
  }

  // 4. No tokens — perform anonymous login first (deduplicated)
  return doAnonymousLogin().pipe(
    switchMap((response) => {
      // For SSR, also save to node process memory cache
      if (!isBrowser) {
        serverAnonymousToken = response.access_token;
      }
      return executeWithToken(response.access_token);
    }),
    catchError((error) => {
      console.error('Anonymous login failed in interceptor:', error);
      // Proceed without token — endpoint may work for public data or fail gracefully
      return handleRequest(req, next);
    })
  );
};
